import * as React from 'react';
import { Input } from 'reactstrap';
import { Moment } from 'moment';
import { localMoment } from 'util/Time';
import AppState from 'AppState';

export interface TimePickerProps {
	onTimeChange: (value: any) => void;
	value: Moment | null;
	timeIncrement: number;
	disabled?: boolean;
}

export const AM = 'AM';
export const PM = 'PM';

interface TimePickerItemProps {
	children: React.ReactNode;
}
class TimePickerItem extends React.Component<TimePickerItemProps> {
	constructor(props: TimePickerItemProps) {
		super(props);
	}

	public render() {
		return (
			<div className="time-picker__item">
				<div className="time-picker__group">{this.props.children}</div>
			</div>
		);
	}
}

/**
 * Helper control for dealing with times. Used primarily by DateTime.tsx, but could be used anywhere.
 *
 * @class TimePicker
 * @extends {React.Component<TimePickerProps>}
 */
class TimePicker extends React.Component<TimePickerProps> {
	private static SecondsPerMinute = 60;
	private static MinutesPerHour = 60;
	private static HoursPerDay = 24;

	/** The default hour if the user doesn't provide one (12PM) */
	private static DefaultHour = 12;

	/** The default minutes if the user doesn't provide one (0) */
	private static DefaultMinutes = 0;

	private availableTimes: number[] = [];

	public static defaultProps: Partial<TimePickerProps> = {
		timeIncrement: 15,
	};

	constructor(props: TimePickerProps) {
		super(props);

		const { timeIncrement } = props;

		if (timeIncrement <= 0) {
			throw new Error('The time increment must be greater than zero!');
		}

		if (TimePicker.MinutesPerHour % timeIncrement !== 0) {
			throw new Error('The time increment cannot result in fractional minutes!');
		}

		for (let i = 0; i < TimePicker.MinutesPerHour; i += timeIncrement) {
			this.availableTimes.push(i);
		}
	}

	/**
	 * Event handler for changes on the hour dropdown
	 */
	public handleHourUpdate = (e: React.FormEvent<HTMLInputElement>): void => {
		const selectedHour = e.currentTarget.value;
		const { value, onTimeChange } = this.props;

		if (!value) {
			onTimeChange(localMoment().hour(parseInt(selectedHour)).minute(TimePicker.DefaultMinutes));
		} else {
			let newValue = value.clone().tz(AppState.settings.timeZone);

			// Add 12 hours when in PM
			const meridiem = value.hour() >= 12 ? 12 : 0;

			newValue = newValue.hour((parseInt(selectedHour) % 12) + meridiem);

			onTimeChange(newValue);
		}
	};

	/**
	 * Event handler for changes on the minute dropdown
	 */
	public handleMinuteUpdate = (e: React.FormEvent<HTMLInputElement>): void => {
		const selectedMinute = e.currentTarget.value;
		const { value, onTimeChange } = this.props;

		if (!value) {
			onTimeChange(localMoment().minute(parseInt(selectedMinute)).hour(TimePicker.DefaultHour));
		} else {
			onTimeChange(value.clone().minute(parseInt(selectedMinute)));
		}
	};

	/**
	 * Event handler for changes on the meridiem dropdown (AM/PM)
	 */
	public handleMeridiemUpdate = (e: React.FormEvent<HTMLInputElement>): void => {
		const selectedMeridiem = e.currentTarget.value;
		const { value, onTimeChange } = this.props;

		const newDate = localMoment(value || undefined);

		if (selectedMeridiem === AM && newDate.hour() >= 12) {
			newDate.hour(newDate.hour() - 12);
		} else if (selectedMeridiem === PM && newDate.hour() < 12) {
			newDate.hour(newDate.hour() + 12);
		}

		onTimeChange(newDate);
	};

	/**
	 * Render method of the TimePicker component. Renders the time input as three separate inputs for hour, minute, and meridiem respectively.
	 *
	 * @returns
	 * @memberof TimePicker
	 */
	public render() {
		const { value } = this.props;

		const valueDate = value ? localMoment(value) : null;

		let hour: number | string;
		let minute: number | string;
		let meridiem: string;

		if (valueDate && valueDate.isValid()) {
			const currentHour = valueDate.hour();
			hour = currentHour >= 12 ? currentHour - 12 : currentHour;
			if (hour === 0) {
				hour = 12;
			}

			minute = valueDate.minute();
			meridiem = valueDate.hour() >= 12 ? 'PM' : 'AM';
		} else {
			hour = '';
			minute = '';
			meridiem = '';
		}

		return (
			<div className="time-picker">
				<TimePickerItem>
					<Input
						type="select"
						className="time-picker__input"
						disabled={this.props.disabled}
						value={hour}
						onChange={this.handleHourUpdate}
					>
						<option value="" />
						<option value={12}>12</option>
						<option value={1}>1</option>
						<option value={2}>2</option>
						<option value={3}>3</option>
						<option value={4}>4</option>
						<option value={5}>5</option>
						<option value={6}>6</option>
						<option value={7}>7</option>
						<option value={8}>8</option>
						<option value={9}>9</option>
						<option value={10}>10</option>
						<option value={11}>11</option>
					</Input>
				</TimePickerItem>
				<TimePickerItem>
					<Input
						type="select"
						className="time-picker__input"
						disabled={this.props.disabled}
						value={minute}
						onChange={this.handleMinuteUpdate}
					>
						<option value="" />
						{this.availableTimes.map((availableTime, index) => (
							<option key={index} value={availableTime}>
								{(availableTime < 10 ? '0' : '') + availableTime.toString()}
							</option>
						))}
					</Input>
				</TimePickerItem>
				<TimePickerItem>
					<Input
						type="select"
						className="time-picker__input"
						disabled={this.props.disabled}
						value={meridiem}
						onChange={this.handleMeridiemUpdate}
					>
						<option value="" />
						<option value={AM}>AM</option>
						<option value={PM}>PM</option>
					</Input>
				</TimePickerItem>
			</div>
		);
	}
}

export default TimePicker;
